<template>
  <div class="operations-dashboard" v-loading="loading">
    <el-row class="page-heading" type="flex" align="middle" justify="space-between">
      <el-col :span="12"><Title title="运营数据总览"></Title></el-col>
      <el-col :span="12" class="heading-note">汇总数据缓存 5 分钟，在线人数及行为记录按请求读取</el-col>
    </el-row>

    <el-card class="toolbar-card" shadow="never">
      <div class="toolbar">
        <div class="toolbar-field">
          <span class="toolbar-label">在线构成采样上限</span>
          <el-input-number v-model="search_op.limit" :min="1" :max="50000" :step="1000" controls-position="right"></el-input-number>
        </div>
        <el-button type="primary" icon="el-icon-refresh" @click="getList">刷新数据</el-button>
        <span class="toolbar-summary">本次采样 {{ formatNumber(total) }} 人</span>
        <span class="toolbar-time" v-if="generatedAt">统计时间：{{ generatedAt }}</span>
      </div>
    </el-card>

    <el-row :gutter="16" class="metric-grid">
      <el-col v-for="item in metricCards" :key="item.key" :xs="24" :sm="12" :md="6">
        <el-card class="metric-card" shadow="hover">
          <div class="metric-icon" :class="item.tone"><i :class="item.icon"></i></div>
          <div class="metric-content">
            <div class="metric-label">{{ item.label }}</div>
            <div class="metric-value">{{ formatNumber(item.value) }}</div>
            <div class="metric-help">{{ item.help }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="content-row">
      <el-col :xs="24" :lg="18">
        <el-card class="panel-card" shadow="never">
          <div slot="header" class="panel-header">
            <div><strong>最近 24 小时在线趋势</strong><span>按最近 288 个采样点展示</span></div>
          </div>
          <div class="trend-chart" v-if="onlineTrend.length"><LineChart ref="onlineTrendChart" :height="320" /></div>
          <div class="empty-panel" v-else>暂时没有在线趋势记录</div>
        </el-card>
      </el-col>
      <el-col :xs="24" :lg="6">
        <el-card class="panel-card health-card" shadow="never">
          <div slot="header" class="panel-header">
            <div><strong>业务数据补充</strong><span>帮助判断内容和用户状态</span></div>
          </div>
          <div class="health-list">
            <div v-for="item in healthItems" :key="item.label" class="health-item">
              <span>{{ item.label }}</span>
              <strong :class="{'warning-text': item.warning}">{{ formatNumber(item.value) }}</strong>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <div class="section-title">
      <strong>当前在线用户构成</strong>
      <span>数量和占比固定展示，不需要把鼠标移到图形上</span>
    </div>
    <el-row :gutter="16" class="distribution-grid">
      <el-col v-for="distribution in distributions" :key="distribution.key" :xs="24" :lg="8">
        <el-card class="panel-card distribution-card" shadow="never">
          <div slot="header" class="panel-header">
            <div><strong>{{ distribution.title }}</strong><span>共 {{ formatNumber(distribution.total) }} 个样本</span></div>
          </div>
          <div v-if="distribution.rows.length" class="distribution-content">
            <div class="distribution-chart"><Persent :ref="distribution.ref" :height="230" /></div>
            <div class="distribution-list">
              <div v-for="row in distribution.rows" :key="row.key" class="distribution-row">
                <span class="legend-dot" :style="{backgroundColor: row.color}"></span>
                <span class="distribution-name" :title="row.label">{{ row.label }}</span>
                <strong>{{ formatNumber(row.count) }}</strong>
                <span class="distribution-percent">{{ row.percent }}%</span>
              </div>
            </div>
          </div>
          <div class="empty-panel" v-else>暂无数据</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 2026-10-07：用户行为明细仅最高管理员可见；保留原统计页的等级2访问权限。 -->
    <SoundActivity v-if="$store.getters.isTheHighestLevel" />

    <el-card class="panel-card top-sounds-card" shadow="never">
      <div slot="header" class="panel-header">
        <div><strong>热门音效</strong><span>按系统累计下载次数排列，便于观察长期受欢迎内容</span></div>
      </div>
      <el-table :data="topSounds" stripe size="small" empty-text="暂无热门音效数据">
        <el-table-column type="index" label="排名" width="70"></el-table-column>
        <el-table-column prop="name" label="音效名称" min-width="220"></el-table-column>
        <el-table-column label="累计下载" width="140" align="right">
          <template slot-scope="scope">{{ formatNumber(scope.row.download_times) }}</template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>
  
<script>
import Title from './components/Title'
import apis from '@/api/index'
import Persent from './components/Persent.vue'
import LineChart from './components/Line.vue'
import SoundActivity from './components/SoundActivity.vue'

export default {
  name: 'DataPersent',
  components: { Title, Persent, LineChart, SoundActivity },
  data() {
    return {
      loading: false,
      total: 0,
      generatedAt: '',
      search_op: { limit: 10000 },
      overview: { online: {}, users: {}, content: {}, service: {} },
      onlineTrend: [],
      topSounds: [],
      distributions: [
        { key: 'arch', ref: 'archChart', title: '系统架构', total: 0, rows: [] },
        { key: 'platform', ref: 'platformChart', title: '操作系统', total: 0, rows: [] },
        { key: 'softwareVersion', ref: 'versionChart', title: '软件版本', total: 0, rows: [] }
      ],
      colors: [
        '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444',
        '#06b6d4', '#f97316', '#6366f1', '#84cc16', '#ec4899',
        '#64748b', '#14b8a6', '#a855f7', '#eab308', '#0ea5e9'
      ]
    }
  },
  computed: {
    metricCards() {
      const online = this.overview.online || {}
      const users = this.overview.users || {}
      const content = this.overview.content || {}
      return [
        { key: 'online', label: '当前在线', value: online.current, help: 'Redis 实时在线记录', icon: 'el-icon-monitor', tone: 'blue' },
        { key: 'peak', label: '24 小时峰值', value: online.peak_24h, help: '最近 288 个采样点', icon: 'el-icon-top', tone: 'violet' },
        { key: 'average', label: '24 小时平均在线', value: online.average_24h, help: '观察整体在线水平', icon: 'el-icon-data-line', tone: 'cyan' },
        { key: 'users', label: '用户总数', value: users.total, help: '全部注册账号', icon: 'el-icon-user', tone: 'green' },
        { key: 'registered', label: '今日新增用户', value: users.registered_today, help: '从今日 00:00 开始', icon: 'el-icon-user-solid', tone: 'orange' },
        { key: 'active', label: '近 7 日登录用户', value: users.active_7d, help: '按最近登录时间统计', icon: 'el-icon-date', tone: 'indigo' },
        { key: 'sounds', label: '音效总数', value: content.sounds_total, help: '包含启用和停用音效', icon: 'el-icon-headset', tone: 'pink' },
        { key: 'downloads', label: '今日下载记录', value: content.downloads_today, help: '从今日 00:00 开始', icon: 'el-icon-download', tone: 'red' }
      ]
    },
    healthItems() {
      const users = this.overview.users || {}
      const content = this.overview.content || {}
      const service = this.overview.service || {}
      return [
        { label: '正常启用用户', value: users.enabled },
        { label: '已激活用户', value: users.activated },
        { label: '已上架音效', value: content.sounds_enabled },
        { label: '累计下载次数', value: content.downloads_total },
        { label: '待处理找音效消息', value: service.find_sound_unread_messages, warning: Number(service.find_sound_unread_messages || 0) > 0 }
      ]
    }
  },
  methods: {
    /**
     * 2026-10-06：一次获取运营总览、在线趋势和在线构成，避免多个统计请求互相覆盖。
     * 例如软件版本占比只有 0.1% 时，固定明细列表仍会直接显示它的数量和占比。
     */
    async getList() {
      this.loading = true
      try {
        const response = await apis.onlineUser.getOnlineDataPersent({ limit: this.search_op.limit })
        if (!response.data || response.data.code !== 1) {
          throw new Error((response.data && response.data.msg) || '运营数据获取失败')
        }

        const data = response.data.data || {}
        const list = data.list || {}
        this.total = Number(list.total || 0)
        this.generatedAt = data.generated_at || ''
        this.overview = data.overview || { online: {}, users: {}, content: {}, service: {} }
        this.onlineTrend = data.online_trend || []
        this.topSounds = data.top_sounds || []
        if (data.overview_error) {
          this.$message.warning(data.overview_error)
        }
        this.setDistribution('arch', list.arch || {})
        this.setDistribution('platform', list.platform || {})
        this.setDistribution('softwareVersion', list.softwareVersion || {})

        this.$nextTick(() => {
          this.renderDistributionCharts()
          this.renderOnlineTrend()
        })
      } catch (error) {
        this.$message.error(error.message || String(error))
      } finally {
        this.loading = false
      }
    },

    /**
     * 2026-10-06：把服务端计数转换为固定可见的“名称、数量、占比”列表。
     * 例如只有 1 人使用 ia32，也会在列表中显示，而不会因为圆环太窄无法悬停。
     */
    setDistribution(key, source) {
      const distribution = this.distributions.find(item => item.key === key)
      if (!distribution) return

      const rows = Object.keys(source).map((itemKey, index) => ({
        key: itemKey,
        label: this.formatDistributionLabel(key, itemKey),
        count: Number(source[itemKey] || 0),
        color: this.colors[index % this.colors.length]
      })).sort((a, b) => b.count - a.count)
      const total = rows.reduce((sum, row) => sum + row.count, 0)
      rows.forEach((row, index) => {
        row.color = this.colors[index % this.colors.length]
        row.percent = total > 0 ? (row.count * 100 / total).toFixed(2) : '0.00'
      })
      distribution.total = total
      distribution.rows = rows
    },

    /**
     * 2026-10-06：圆环图只负责快速看结构，精确数字由下方固定列表承载。
     * 例如版本很多时，图例不会挤占图表区域，所有版本仍按数量从高到低列出。
     */
    renderDistributionCharts() {
      this.distributions.forEach(distribution => {
        const chart = this.$refs[distribution.ref]
        const chartComponent = Array.isArray(chart) ? chart[0] : chart
        if (!chartComponent || !distribution.rows.length) return
        chartComponent.setData({
          labels: distribution.rows.map(row => row.label),
          datasets: [{
            backgroundColor: distribution.rows.map(row => row.color),
            borderColor: '#ffffff',
            borderWidth: 2,
            data: distribution.rows.map(row => row.count)
          }]
        })
      })
    },

    /**
     * 2026-10-06：将最近在线记录绘制为一条紧凑趋势线。
     * 例如一天内的高峰和低谷可以直接从曲线观察，详细人数仍可悬停查看。
     */
    renderOnlineTrend() {
      if (!this.$refs.onlineTrendChart || !this.onlineTrend.length) return
      this.$refs.onlineTrendChart.setData({
        labels: this.onlineTrend.map(row => (row.time || '').slice(5)),
        datasets: [{
          label: '在线人数',
          data: this.onlineTrend.map(row => Number(row.count || 0)),
          borderColor: '#3b82f6',
          backgroundColor: '#3b82f6',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.25,
          fill: false
        }]
      })
    },
    formatDistributionLabel(type, value) {
      const labels = {
        arch: { x64: '64 位', ia32: '32 位', unknownArch: '未知架构' },
        platform: { win32: 'Windows', darwin: 'macOS', unknownPlatform: '未知系统' },
        softwareVersion: { unknownSoftwareVersion: '未知版本' }
      }
      return (labels[type] && labels[type][value]) || value
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('zh-CN')
    }
  },
  activated() {
    this.getList()
  },
  mounted() {
    this.getList()
  }
}
</script>
  
<style lang="scss" scoped>
.operations-dashboard {
  min-height: 100%;
  padding: 15px;
  background: #f5f7fa;
  box-sizing: border-box;
}

.page-heading { min-height: 60px; }
.heading-note { color: #909399; font-size: 13px; text-align: right; padding-right: 4px; }
.toolbar-card, .panel-card, .metric-card { border: 0; }
.toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 14px; }
.toolbar-field { display: flex; align-items: center; gap: 10px; }
.toolbar-label, .toolbar-summary { color: #606266; font-size: 14px; }
.toolbar-time { margin-left: auto; color: #909399; font-size: 13px; }
.metric-grid { margin-top: 2px; }
.metric-grid .el-col { margin-top: 16px; }
.metric-card ::v-deep .el-card__body { display: flex; align-items: center; min-height: 112px; padding: 18px; }

.metric-icon {
  width: 50px;
  height: 50px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-right: 14px;
  font-size: 24px;
}
.metric-icon.blue { color: #2563eb; background: #dbeafe; }
.metric-icon.violet { color: #7c3aed; background: #ede9fe; }
.metric-icon.cyan { color: #0891b2; background: #cffafe; }
.metric-icon.green { color: #059669; background: #d1fae5; }
.metric-icon.orange { color: #d97706; background: #fef3c7; }
.metric-icon.indigo { color: #4f46e5; background: #e0e7ff; }
.metric-icon.pink { color: #db2777; background: #fce7f3; }
.metric-icon.red { color: #dc2626; background: #fee2e2; }
.metric-content { min-width: 0; }
.metric-label { color: #606266; font-size: 14px; }
.metric-value { color: #1f2937; font-size: 27px; line-height: 36px; font-weight: 700; }
.metric-help { color: #a0a4ab; font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.content-row, .distribution-grid { margin-top: 16px; }
.content-row .el-col, .distribution-grid .el-col { margin-bottom: 16px; }
.panel-header { display: flex; align-items: center; justify-content: space-between; }
.panel-header strong { color: #303133; font-size: 16px; }
.panel-header span { display: block; margin-top: 5px; color: #909399; font-size: 12px; }
.trend-chart { height: 320px; }
.health-card { min-height: 395px; }
.health-list { display: flex; flex-direction: column; }
.health-item { display: flex; align-items: center; justify-content: space-between; min-height: 51px; border-bottom: 1px solid #ebeef5; color: #606266; }
.health-item:last-child { border-bottom: 0; }
.health-item strong { color: #303133; font-size: 18px; }
.warning-text { color: #e6a23c !important; }
.section-title { display: flex; align-items: baseline; gap: 12px; margin: 4px 0 0; }
.section-title strong { color: #303133; font-size: 18px; }
.section-title span { color: #909399; font-size: 13px; }
.distribution-card { min-height: 510px; }
.distribution-chart { height: 230px; }
.distribution-list { max-height: 205px; margin-top: 10px; overflow-y: auto; padding-right: 4px; }
.distribution-row { display: grid; grid-template-columns: 10px minmax(80px, 1fr) auto 62px; align-items: center; gap: 8px; min-height: 32px; color: #606266; font-size: 13px; }
.legend-dot { width: 9px; height: 9px; border-radius: 50%; }
.distribution-name { overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.distribution-row strong { color: #303133; }
.distribution-percent { color: #909399; text-align: right; }
.empty-panel { height: 220px; display: flex; align-items: center; justify-content: center; color: #c0c4cc; }
.top-sounds-card { margin-bottom: 16px; }

@media (max-width: 1200px) {
  .heading-note { display: none; }
  .health-card { min-height: auto; }
}

@media (max-width: 768px) {
  .operations-dashboard { padding: 10px; }
  .toolbar-time { width: 100%; margin-left: 0; }
  .section-title { align-items: flex-start; flex-direction: column; gap: 5px; }
}
</style>
